import type {AppendMessage, ThreadMessage} from '@assistant-ui/core';
import {
  useExternalMessageConverter,
  useExternalStoreRuntime,
} from '@assistant-ui/core/react';
import {useWbAgentClient} from '@wandb/weave/lib/WbAgent/useWbAgentClient';
import {
  type Dispatch,
  type MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {useNavContext} from '../components/NavContextProvider';
import {buildThreadFromTurns} from './buildThread';
import {toAUIMessage} from './messageConversions';
import {extractErrorMessage} from './runtime/errors';
import type {RuntimeEvent} from './runtime/events';
import type {AgentRuntime} from './runtime/runtime';
import type {AgentChatAction} from './store/actions';
import {
  addUserMessage,
  beginLoadThread,
  createThread,
  failLoadThread,
  loadThread,
  setActiveThread,
  setThreadError,
  setThreadStatus,
} from './store/actions';
import {
  type AgentChatStore,
  useActiveThread,
  useActiveThreadLoadState,
  useAgentChatDispatch,
  useAgentChatStore,
} from './store/context';
import {getActiveBranch} from './store/messageTree';
import {selectThreadIdByRootTurnId} from './store/selectors';
import type {MessageNode, TextPart, Thread} from './store/thread';
import {StreamingBuffer} from './streamingBuffer';

/**
 * useAgentChatRuntime makes an `ExternalStoreRuntime` for `assistant-ui`
 * backed by the agent chat store and the given {@link AgentRuntime} (if ready).
 */
export function useAgentChatRuntime(
  runtime: AgentRuntime | null
): ReturnType<typeof useExternalStoreRuntime> {
  const dispatch = useAgentChatDispatch();
  const activeThread = useActiveThread();
  const {activeThreadId: storeThreadId, isLoading: isActiveThreadLoading} =
    useActiveThreadLoadState();
  const store = useAgentChatStore();
  const {params} = useNavContext();

  // Use a ref so callbacks always read the latest `activeThread` value.
  const activeThreadRef = useRef(activeThread);
  activeThreadRef.current = activeThread;

  // Lag the thread-list adapter's thread ID by one tick on switch so it changes
  // in sync with real messages, not during the empty-messages transition tick.
  // This avoids a crash in AUI's `setAdapter` [1] where a new internal thread is
  // created from a stale factory before messages are updated.
  //
  // Both renders commit before paint via `useLayoutEffect`, so there's no
  // flicker.
  //
  // [1] https://github.com/assistant-ui/assistant-ui/issues/3652
  const [committedThreadId, setCommittedThreadId] = useState(storeThreadId);
  useLayoutEffect(() => {
    if (committedThreadId !== storeThreadId) {
      setCommittedThreadId(storeThreadId);
    }
  }, [storeThreadId, committedThreadId]);
  const isThreadTransitioning = committedThreadId !== storeThreadId;

  const isRunning =
    activeThread?.status === 'running' && !isThreadTransitioning;

  const activeBranch = useMemo(
    () =>
      activeThread && !isThreadTransitioning
        ? getActiveBranch(activeThread.messageTree)
        : [],
    [activeThread, isThreadTransitioning]
  );
  const threadError =
    activeThread?.status === 'error' ? activeThread.error : undefined;

  const metadata = useMemo(
    () => ({error: threadError ?? undefined}),
    [threadError]
  );

  const convertedMessages = useExternalMessageConverter<MessageNode>({
    callback: toAUIMessage,
    messages: activeBranch,
    isRunning,
    metadata,
  });

  const isDisabled =
    !params.entityName || !params.projectName || isActiveThreadLoading;

  // Used to cancel async generators if the runtime provider unmounts.
  const shutdownRef = useRef(new AbortController());

  useEffect(() => {
    const controller = shutdownRef.current;
    return () => {
      controller.abort();
    };
  }, []);

  const drainEvents = useCallback(
    async (
      threadId: string,
      generator: AsyncGenerator<RuntimeEvent, void, undefined>
    ): Promise<'idle' | 'error' | 'cancelled'> => {
      // Scope every dispatch to the run that produced it. The reducer drops
      // events whose runId already has an outcome in thread.runOutcomes.
      let runId = '';
      const buffer = new StreamingBuffer(event => {
        dispatch({
          type: 'applyRuntimeEvent',
          threadId,
          event,
          runId,
          timestamp: Date.now(),
        });
      });

      let outcome: 'idle' | 'error' | 'cancelled' = 'idle';
      try {
        for await (const event of generator) {
          if (event.type === 'run-started') {
            runId = event.runId;
          }
          if (event.type === 'run-error') {
            outcome = 'error';
          }
          if (event.type === 'run-cancelled') {
            outcome = 'cancelled';
          }
          buffer.push(event);
        }
      } finally {
        buffer.dispose();
      }

      return outcome;
    },
    [dispatch]
  );

  const withStatusDispatch = useCallback(
    async (
      threadId: string,
      run: () => Promise<'idle' | 'error' | 'cancelled'>
    ): Promise<void> => {
      dispatch(setThreadStatus(threadId, 'running'));
      dispatch(setThreadError(threadId, null));

      try {
        const result = await run();
        if (result === 'cancelled') {
          return;
        }
        if (result !== 'error') {
          dispatch(setThreadStatus(threadId, 'idle'));
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          dispatch(setThreadStatus(threadId, 'idle'));
        } else {
          dispatch(setThreadError(threadId, extractErrorMessage(err)));
          dispatch(setThreadStatus(threadId, 'error'));
        }
      }
    },
    [dispatch]
  );

  const startTurn = useCallback(
    async (
      threadId: string,
      opts: {userMessage?: string; parentId?: string | null}
    ) => {
      if (!runtime) {
        dispatch(setThreadError(threadId, null));
        dispatch(setThreadStatus(threadId, 'idle'));
        return;
      }

      await withStatusDispatch(threadId, async () => {
        return drainEvents(
          threadId,
          runtime.runTurn({
            threadId,
            userMessage: opts.userMessage,
            parentId: opts.parentId,
            signal: shutdownRef.current.signal,
          })
        );
      });
    },
    [dispatch, runtime, shutdownRef, drainEvents, withStatusDispatch]
  );

  const abortThread = useCallback(
    (threadId: string) => {
      const runId = activeThreadRef.current?.currentRunId;
      if (runId) {
        dispatch({
          type: 'applyRuntimeEvent',
          threadId,
          event: {type: 'run-cancelled', runId},
          runId,
          timestamp: Date.now(),
        });
      }
      runtime?.cancel?.(threadId);
    },
    [runtime, dispatch, activeThreadRef]
  );

  const switchToThread = useSwitchToThread(
    runtime,
    dispatch,
    store,
    shutdownRef,
    drainEvents,
    withStatusDispatch
  );

  const {onNew, onEdit, onReload, onCancel} = useThreadCallbacks(
    activeThreadRef,
    dispatch,
    startTurn,
    abortThread
  );

  const onAddToolResult = useCallback(
    (options: {
      messageId: string;
      toolCallId: string;
      result: unknown;
      isError?: boolean;
    }) => {
      const thread = activeThreadRef.current;
      if (!thread?.id || !thread.currentRunId) {
        return;
      }

      dispatch({
        type: 'applyRuntimeEvent',
        threadId: thread.id,
        runId: thread.currentRunId,
        event: {
          type: 'tool-call-result',
          content: String(options.result),
          isError: options.isError ?? false,
          toolCallId: options.toolCallId,
        },
        timestamp: Date.now(),
      });
    },
    [dispatch, activeThreadRef]
  );

  const setMessages = useCallback(
    (messages: readonly ThreadMessage[]) => {
      const threadId = activeThreadRef.current?.id;
      if (!threadId || messages.length === 0) {
        return;
      }

      const targetNodeId = messages[messages.length - 1].id;
      if (targetNodeId !== activeThreadRef.current?.messageTree.currentNodeId) {
        dispatch({
          type: 'switchBranch',
          timestamp: Date.now(),
          targetNodeId,
          threadId,
        });
      }
    },
    [dispatch, activeThreadRef]
  );

  // Switch the thread-list ID in sync with real messages, not during the
  // empty-messages transition tick; see above comment.
  const threadListAdapter = useMemo(
    () => ({
      threadId: committedThreadId ?? '',
      threads: committedThreadId
        ? [{id: committedThreadId, status: 'regular' as const}]
        : [],
      onSwitchToNewThread: () => dispatch(createThread()),
      onSwitchToThread: switchToThread,
    }),
    [committedThreadId, dispatch, switchToThread]
  );

  const auiRuntime = useExternalStoreRuntime({
    isDisabled,
    messages: convertedMessages,
    setMessages,
    isRunning,
    onNew,
    onEdit,
    onReload,
    onCancel,
    onAddToolResult,
    adapters: {
      threadList: threadListAdapter,
    },
  });

  return auiRuntime;
}

type DrainEvents = (
  threadId: string,
  generator: AsyncGenerator<RuntimeEvent, void, undefined>
) => Promise<'idle' | 'error' | 'cancelled'>;

type WithStatusDispatch = (
  threadId: string,
  run: () => Promise<'idle' | 'error' | 'cancelled'>
) => Promise<void>;

/**
 * useSwitchToThread loads a historical conversation by its root turn ID, or
 * activates an already-loaded thread. Each new switch aborts any previous
 * in-flight fetch, so rapid switching is safe.
 */
function useSwitchToThread(
  runtime: AgentRuntime | null,
  dispatch: Dispatch<AgentChatAction>,
  store: AgentChatStore,
  shutdownRef: MutableRefObject<AbortController>,
  drainEvents: DrainEvents,
  withStatusDispatch: WithStatusDispatch
): (rootTurnId: string) => Promise<void> {
  const {params} = useNavContext();
  const client = useWbAgentClient(params.entityName, params.projectName);
  const switchControllerRef = useRef<AbortController | null>(null);

  return useCallback(
    async (rootTurnId: string) => {
      const existingId = selectThreadIdByRootTurnId(
        store.getState(),
        rootTurnId
      );
      if (existingId) {
        dispatch(setActiveThread(existingId));
        return;
      }

      if (!client) {
        return;
      }

      // Abort any previous in-flight switch.
      switchControllerRef.current?.abort();
      const controller = new AbortController();
      switchControllerRef.current = controller;

      // Also abort if the component unmounts.
      shutdownRef.current.signal.addEventListener(
        'abort',
        () => controller.abort(),
        {once: true}
      );

      dispatch(beginLoadThread(rootTurnId));

      try {
        const turns = await client.get(
          '/turns',
          {
            root_turn_id: rootTurnId,
            sort_by: 'created_at',
            sort_desc: false,
            // TODO: Handle pagination for turns.
            limit: 100,
          },
          controller.signal
        );

        const thread = buildThreadFromTurns(rootTurnId, turns);
        dispatch(loadThread(thread));

        // Resume polling if the last turn is still in-progress.
        //
        // TODO: Re-use existing polling generator if one exists.
        if (
          thread.status === 'running' &&
          turns.length > 0 &&
          runtime?.resumeTurn
        ) {
          const lastTurn = turns[turns.length - 1];
          await withStatusDispatch(rootTurnId, async () => {
            const gen = runtime.resumeTurn!({
              turnId: lastTurn.id,
              threadId: rootTurnId,
              signal: controller.signal,
              initialSnapshot: lastTurn,
            });
            return drainEvents(rootTurnId, gen);
          });
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          return;
        }
        dispatch(failLoadThread(extractErrorMessage(err)));
      }
    },
    [
      client,
      runtime,
      dispatch,
      store,
      shutdownRef,
      drainEvents,
      withStatusDispatch,
    ]
  );
}

type StartTurn = (
  threadId: string,
  opts: {userMessage?: string; parentId?: string | null}
) => Promise<void>;

/**
 * useThreadCallbacks creates all four AUI thread callbacks: `onNew`, `onEdit`,
 * `onReload`, and `onCancel`.
 */
function useThreadCallbacks(
  activeThreadRef: MutableRefObject<Thread | undefined>,
  dispatch: Dispatch<AgentChatAction>,
  startTurn: StartTurn,
  abortThread: (threadId: string) => void
) {
  const appendAndRunTurn = useCallback(
    async (threadId: string, text: string, parentId: string | null) => {
      dispatch(addUserMessage(threadId, parentId, text));
      await startTurn(threadId, {userMessage: text, parentId});
    },
    [dispatch, startTurn]
  );

  const onNew = useCallback(
    async (message: AppendMessage) => {
      const text = extractText(message);
      if (!text.trim()) {
        return;
      }

      const currentThread = activeThreadRef.current;
      let threadId = currentThread?.id ?? null;
      if (!threadId) {
        const action = createThread();
        threadId = action.id;
        dispatch(action);
      }

      const parentId = currentThread?.messageTree.currentNodeId ?? null;
      await appendAndRunTurn(threadId, text, parentId);
    },
    [dispatch, appendAndRunTurn, activeThreadRef]
  );

  const onEdit = useCallback(
    async (message: AppendMessage) => {
      const threadId = activeThreadRef.current?.id;
      if (!threadId) {
        return;
      }

      const text = extractText(message);
      if (!text.trim()) {
        return;
      }

      await appendAndRunTurn(threadId, text, message.parentId ?? null);
    },
    [appendAndRunTurn, activeThreadRef]
  );

  const onReload = useCallback(
    async (parentId: string | null, _config?: unknown) => {
      const thread = activeThreadRef.current;
      if (!thread) {
        return;
      }

      const node = parentId ? thread.messageTree.nodes[parentId] : undefined;
      const textPart =
        node?.role === 'user'
          ? node.content.find((p): p is TextPart => p.type === 'text')
          : undefined;
      const userMessage = textPart?.text;

      await startTurn(thread.id, {userMessage, parentId});
    },
    [startTurn, activeThreadRef]
  );

  const onCancel = useCallback(async () => {
    const threadId = activeThreadRef.current?.id;
    if (threadId) {
      abortThread(threadId);
    }
  }, [abortThread, activeThreadRef]);

  return {onNew, onEdit, onReload, onCancel};
}

/** extractText extracts plain text from an AUI `AppendMessage`'s content parts. */
function extractText(message: AppendMessage): string {
  return (message.content as readonly {type: string; text?: string}[])
    .filter(p => p.type === 'text')
    .map(p => p.text ?? '')
    .join('\n\n');
}
